import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { authService } from '../../services/authService';
import { ApiError } from '../../services/apiClient';
import spottoLogo from '../../assets/spotto-logo.png';

const APP_STORE_URL = 'https://apps.apple.com/app/spotto/id6740039498';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=pl.spotto.mobile';

export const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError(t('auth.verifyEmail.invalidLink'));
        setIsLoading(false);
        return;
      }

      try {
        const response = await authService.verifyEmail(token);
        setSuccessMessage(response.message || t('auth.verifyEmail.success'));
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.data && typeof err.data === 'object' && 'error' in err.data) {
            const errorCode = (err.data as { error: string }).error;
            if (errorCode === 'INVALID_TOKEN' || errorCode === 'TOKEN_EXPIRED') {
              setError(t('auth.verifyEmail.invalidOrExpiredToken'));
            } else if (errorCode === 'ALREADY_VERIFIED') {
              setError(t('auth.verifyEmail.alreadyVerified'));
            } else {
              setError(err.message || t('auth.validation.unexpectedError'));
            }
          } else {
            setError(err.message || t('auth.validation.unexpectedError'));
          }
        } else {
          setError(t('auth.validation.unexpectedError'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [searchParams, t]);

  return (
    <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src={spottoLogo}
              alt="Spotto Logo"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('app.name')}</h1>
          <p className="text-[#9aa3b2]">SCHEDULE - SPORT - PROGRESS</p>
        </div>

        {/* Verification Card */}
        <div className="bg-[#2c3036] rounded-2xl border border-white/[0.08] p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-2">{t('auth.verifyEmail.title')}</h2>
          <p className="text-sm text-[#9aa3b2] mb-6">
            {t('auth.verifyEmail.description')}
          </p>

          {isLoading && (
            <div className="flex flex-col items-center justify-center py-8">
              <svg className="animate-spin h-10 w-10 text-[#7d8dcd] mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-[#9aa3b2]">{t('auth.verifyEmail.verifying')}</p>
            </div>
          )}

          {error && !isLoading && (
            <div className="mb-6 p-4 bg-[#c06f57]/10 border border-[#c06f57]/30 rounded-lg">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-[#e1a08a] mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-[#e1a08a]">{t('auth.verifyEmail.failed')}</p>
              </div>
              <p className="text-sm text-[#e1a08a]">{error}</p>
            </div>
          )}

          {successMessage && !isLoading && (
            <div className="mb-6 p-4 bg-[#5fa78a]/10 border border-[#5fa78a]/30 rounded-lg">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-[#86c5ad] mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-[#86c5ad]">{t('auth.verifyEmail.verified')}</p>
              </div>
              <p className="text-sm text-[#86c5ad]">{successMessage}</p>
              <p className="text-sm text-[#c5cde0] mt-3">{t('auth.verifyEmail.loginToMobileApp')}</p>
            </div>
          )}
        </div>

        {/* App Store Links */}
        {!isLoading && (
          <div className="mt-8">
            <p className="text-center text-[#9aa3b2] text-sm mb-4">{t('auth.verifyEmail.downloadApp')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-black hover:bg-[#1a1a1a] text-white px-5 py-2.5 rounded-xl transition-colors border border-white/[0.10]"
              >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-[#9aa3b2]">{t('bookBusiness.downloadOn')}</div>
                  <div className="text-base font-semibold">App Store</div>
                </div>
              </a>

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-black hover:bg-[#1a1a1a] text-white px-5 py-2.5 rounded-xl transition-colors border border-white/[0.10]"
              >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-[#9aa3b2]">{t('bookBusiness.getItOn')}</div>
                  <div className="text-base font-semibold">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
